import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteIteration as persistDeleteIteration,
  loadIterations,
  saveActiveIteration,
} from '../../components/billingReconciliation/persistence.js';
import {
  createNextIteration,
  ensureSeededIterations,
  inheritReviewsFromPriorRound,
  resolveActiveIteration,
} from '../../components/billingReconciliation/iterationsBootstrap.js';
import { isBillingReconciliationMockMode } from '../api/billingReconciliationApi.js';
import {
  useBillingReconciliationRuns,
  useCreateBillingReconciliationRun,
  useDeleteBillingReconciliationRun,
} from '../api/useBillingReconciliationHooks.js';

// Owns all iteration (reconciliation round) state:
// - list of iterations for this user
// - the active iteration for the current service month
// - delete confirmation flow
// - inheritance-from-prior-round toast payload
//
// Every iteration owns its own review state; this hook drives which bucket
// `useReviewsState` should read from (via `activeIterationId`).
const normalizeIterations = (payload) => {
  const rows = Array.isArray(payload) ? payload : payload?.rows || [];
  return rows.map((run, index) => ({
    id: run.id,
    monthId: run.serviceMonth || run.service_month || run.monthId,
    number: run.number || run.roundNumber || run.round_number || index + 1,
    label: run.label || `Round ${run.number || run.roundNumber || index + 1}`,
    createdAt: run.createdAt || run.created_at || new Date().toISOString(),
    ...run,
  }));
};

const makeCreateAttemptKey = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `billing-recon-create-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const runFromCreateError = (error) => error?.response?.data?.run || error?.data?.run || null;

export const useIterationsState = ({
  program = 'website',
  serviceMonth,
  gmRows,
  cpqRows,
  dpeDealers = [],
  gmFile = null,
  cpqFile = null,
  cpqSourceMode = 'upload',
  mockSeedData = null,
}) => {
  const mockMode = isBillingReconciliationMockMode();
  const runsQuery = useBillingReconciliationRuns({ program, serviceMonth }, { enabled: !mockMode });
  const refetchRuns = runsQuery.refetch;
  const createRunMutation = useCreateBillingReconciliationRun();
  const deleteRunMutation = useDeleteBillingReconciliationRun();

  const [iterations, setIterations] = useState(() => {
    if (!mockMode) return [];
    return loadIterations();
  });
  const [activeIterationId, setActiveIterationIdState] = useState(() => {
    if (!mockMode) return null;
    return null;
  });
  const [optimisticRuns, setOptimisticRuns] = useState([]);

  useEffect(() => {
    if (!mockMode || !mockSeedData) return;
    ensureSeededIterations({
      seedData: mockSeedData,
      currentMonthId: '2026-04-01',
    });
    const { iterations: next, active } = resolveActiveIteration(serviceMonth);
    setIterations(next);
    setActiveIterationIdState(active.id);
  }, [mockMode, mockSeedData, serviceMonth]);

  useEffect(() => {
    if (mockMode || !runsQuery.data) return;
    const serverIterations = normalizeIterations(runsQuery.data);
    const serverIds = new Set(serverIterations.map((run) => run.id));
    const optimistic = optimisticRuns
      .filter((run) => run.monthId === serviceMonth && !serverIds.has(run.id));
    const next = [...optimistic, ...serverIterations];
    setIterations(next);
    if (!next.some((it) => it.id === activeIterationId)) {
      setActiveIterationIdState(next[0]?.id || null);
    }
  }, [mockMode, runsQuery.data, activeIterationId, optimisticRuns, serviceMonth]);

  // When the service month changes, pick (or create) that month's active iteration.
  useEffect(() => {
    if (!mockMode) {
      setActiveIterationIdState(null);
      return;
    }
    if (!mockSeedData) return;
    const { iterations: next, active } = resolveActiveIteration(serviceMonth);
    setIterations(next);
    setActiveIterationIdState(active.id);
  }, [mockMode, mockSeedData, serviceMonth]);

  const setActiveIterationId = useCallback((id) => {
    setActiveIterationIdState(id);
    if (!mockMode) return;
    const it = loadIterations().find((i) => i.id === id);
    if (it) saveActiveIteration(it.monthId, id);
  }, [mockMode]);

  const registerImportedRun = useCallback((run) => {
    if (!run?.id) return;
    const [normalizedRun] = normalizeIterations([run]);
    setOptimisticRuns((previous) => [
      normalizedRun,
      ...previous.filter((existing) => existing.id !== normalizedRun.id),
    ]);
    setActiveIterationIdState(normalizedRun.id);
  }, []);

  const [inheritanceToast, setInheritanceToast] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const pendingCreateKeyRef = useRef(null);
  const [pendingCreateKey, setPendingCreateKey] = useState(null);
  const hasProcessingRun = iterations.some((iteration) => iteration.processingStatus === 'processing' || iteration.processing_status === 'processing');

  useEffect(() => {
    if (mockMode || !hasProcessingRun) return undefined;
    const timer = window.setInterval(() => {
      refetchRuns();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [mockMode, hasProcessingRun, refetchRuns]);

  const requestDeleteIteration = useCallback((payload) => {
    setPendingDelete(payload);
  }, []);

  const cancelDeleteIteration = useCallback(() => {
    setPendingDelete(null);
  }, []);

  const removeDeletedBackendIteration = useCallback((deletedId) => {
    setOptimisticRuns((previous) => previous.filter((run) => run.id !== deletedId));
    const next = iterations.filter((run) => run.id !== deletedId);
    setIterations(next);
    setActiveIterationIdState((current) => (
      next.some((run) => run.id === current) ? current : next[0]?.id || null
    ));
  }, [iterations]);

  const canCreateIteration = (
    mockMode || Boolean(gmFile && (cpqFile || cpqSourceMode === 'latest_gm_export'))
  ) && !createRunMutation.isPending && !pendingCreateKey && !hasProcessingRun;
  const isCreating = createRunMutation.isPending || Boolean(pendingCreateKey) || hasProcessingRun;

  const confirmDeleteIteration = useCallback((confirmToken = '') => {
    if (!pendingDelete) return;
    const { iteration } = pendingDelete;
    if (!mockMode) {
      deleteRunMutation.mutate(
        { id: iteration.id, confirmToken },
        { onSuccess: () => removeDeletedBackendIteration(iteration.id) },
      );
      setPendingDelete(null);
      return;
    }
    persistDeleteIteration(iteration.id);

    // Refresh list; if the month is now empty, auto-seed a fresh Round 1.
    let list = loadIterations();
    const stillForMonth = list.filter((it) => it.monthId === iteration.monthId);
    let newActiveId = null;
    if (stillForMonth.length === 0) {
      const replacement = createNextIteration(iteration.monthId, 'Round 1');
      list = loadIterations();
      newActiveId = replacement.id;
    } else if (iteration.id === activeIterationId) {
      // Deleted the active one — pick the newest remaining round.
      const newest = stillForMonth[stillForMonth.length - 1];
      newActiveId = newest.id;
    }
    setIterations(list);
    if (newActiveId) setActiveIterationId(newActiveId);
    setPendingDelete(null);
  }, [pendingDelete, mockMode, deleteRunMutation, activeIterationId, setActiveIterationId, removeDeletedBackendIteration]);

  const createIteration = useCallback((overrides = {}) => {
    const createGmFile = Object.prototype.hasOwnProperty.call(overrides, 'gmFile') ? overrides.gmFile : gmFile;
    const createCpqFile = Object.prototype.hasOwnProperty.call(overrides, 'cpqFile') ? overrides.cpqFile : cpqFile;
    const createCpqSourceMode = overrides.cpqSourceMode || cpqSourceMode;
    const createGmRows = overrides.gmRows || gmRows;
    const createCpqRows = overrides.cpqRows || cpqRows;
    if (!mockMode) {
      const canSubmit = Boolean(createGmFile && (createCpqFile || createCpqSourceMode === 'latest_gm_export'));
      if (!canSubmit || isCreating) return;
      if (pendingCreateKeyRef.current) return;
      const idempotencyKey = pendingCreateKeyRef.current || makeCreateAttemptKey();
      pendingCreateKeyRef.current = idempotencyKey;
      setPendingCreateKey(idempotencyKey);
      createRunMutation.mutate(
        {
          program,
          serviceMonth,
          gmFile: createGmFile,
          cpqFile: createCpqSourceMode === 'latest_gm_export' ? null : createCpqFile,
          cpqSourceMode: createCpqSourceMode,
          idempotencyKey,
        },
        {
          onSuccess: (result) => {
            const createdRun = result?.run || result;
            if (createdRun?.id) {
              const [normalizedRun] = normalizeIterations([createdRun]);
              setOptimisticRuns((previous) => [
                normalizedRun,
                ...previous.filter((run) => run.id !== normalizedRun.id),
              ]);
              setActiveIterationIdState(createdRun.id);
            }
          },
          onError: (error) => {
            const failedRun = runFromCreateError(error);
            if (!failedRun?.id) return;
            const [normalizedRun] = normalizeIterations([failedRun]);
            setOptimisticRuns((previous) => [
              normalizedRun,
              ...previous.filter((run) => run.id !== normalizedRun.id),
            ]);
            setActiveIterationIdState(failedRun.id);
          },
          onSettled: () => {
            pendingCreateKeyRef.current = null;
            setPendingCreateKey(null);
          },
        },
      );
      return;
    }
    const created = createNextIteration(serviceMonth);
    const { total, resolved, carriedVariance } = inheritReviewsFromPriorRound({
      newIterationId: created.id,
      monthId: serviceMonth,
      gmRows: createGmRows,
      cpqRows: createCpqRows,
      dpeDealers,
    });
    setIterations(loadIterations());
    setActiveIterationId(created.id);
    setInheritanceToast(
      total > 0
        ? { total, resolved, carriedVariance, roundNumber: created.number }
        : null,
    );
  }, [mockMode, createRunMutation, program, serviceMonth, gmRows, cpqRows, dpeDealers, gmFile, cpqFile, cpqSourceMode, setActiveIterationId, isCreating]);

  const dismissInheritanceToast = useCallback(() => setInheritanceToast(null), []);

  return {
    iterations,
    activeIterationId,
    setActiveIterationId,
    isLoading: runsQuery.isLoading || createRunMutation.isPending || Boolean(pendingCreateKey) || deleteRunMutation.isPending,
    error: runsQuery.error || createRunMutation.error || deleteRunMutation.error,
    inheritanceToast,
    dismissInheritanceToast,
    pendingDelete,
    requestDeleteIteration,
    cancelDeleteIteration,
    confirmDeleteIteration,
    createIteration,
    registerImportedRun,
    canCreateIteration,
    isCreating,
  };
};
